/**
 * journeyConfig — the whole journey as data, one chain per role.
 *
 * Mechanism ported from Signal's design/onboarding-experience branch:
 * stages of Watch → Try → Reward steps and Neuron rewards,
 * ranks derived from real step boundaries.
 *
 * THREE AUDIENCES, THREE CHAINS. What a person is taught has to match what
 * they can actually do, or the tour teaches doors that are not there:
 *
 *   user        talks to agents. Cannot create or edit one, has no Library
 *               entry and no admin surfaces. Their chain is about working
 *               well in a conversation and making the place their own.
 *   admin       everything above, plus creating agents, the Library, and the
 *               administration of ONE tenant — its people and its agents.
 *   group_admin everything above, plus the wider tools (Insights, the Agent
 *               of Agents) and a scope reaching their own organization AND
 *               every organization beneath it.
 *
 * super_admin runs the group_admin chain: they sit above it, and a platform
 * operator does not need to be taught the product from zero.
 *
 * Steps are declared ONCE below and composed into the chains, so a step two
 * roles share cannot drift between them.
 *
 * Rules kept from Signal, verbatim:
 *  - A film exists only where a sentence cannot teach the gesture.
 *  - A step's detection fires inside the product's own action funnel.
 *  - Locks are cosmetic and never touch RequireFeature/RoleGate/RLS.
 *  - Rank thresholds are DERIVED from the eligible chain, never hand-stamped.
 */

import { JOURNEY_EVENTS, type JourneyEventName } from '../detect';

/**
 * Every film the product ships. Named as a union so a step or a Learn Center
 * topic cannot reference a clip that was never rendered — the registry in
 * scripts/journey/film-registry.json holds the same list for the recorder.
 */
export type JourneyClipId =
  | 'journey-orient'
  | 'journey-model-switch'
  | 'journey-attach'
  | 'journey-library'
  | 'journey-create-agent'
  | 'journey-agent-powers'
  | 'journey-share-agent'
  | 'journey-settings';

/** File name a clip id maps to under /clips/journey/. */
export const clipFile = (id: JourneyClipId): string => `${id}.mp4`;

/** Who a chain is for. */
export type JourneyAudience = 'user' | 'admin' | 'group_admin';

/** A caption beat over a silent film. `key` resolves via t(). */
export interface CaptionBeat {
  atMs: number;
  key: string;
}

/**
 * The narration of every film, as timed beats. Films are silent by contract;
 * these ARE the tutorial's voice, drawn under the picture in whichever
 * language the interface is in. Keyed by clip, not by step: the Learn page
 * plays a film with no step in sight. Re-time whenever a film is re-shot —
 * the recorder prints each beat's timestamp as it lands.
 */
export const FILM_CAPTIONS: Record<JourneyClipId, CaptionBeat[]> = {
  'journey-orient': [
    { atMs: 0, key: 'journey.captions.orient.1' },
    { atMs: 6356, key: 'journey.captions.orient.2' },
    { atMs: 11592, key: 'journey.captions.orient.3' },
    { atMs: 23908, key: 'journey.captions.orient.4' },
  ],
  'journey-model-switch': [
    { atMs: 0, key: 'journey.captions.modelSwitch.1' },
    { atMs: 20081, key: 'journey.captions.modelSwitch.2' },
    { atMs: 26029, key: 'journey.captions.modelSwitch.3' },
    { atMs: 28931, key: 'journey.captions.modelSwitch.4' },
  ],
  'journey-attach': [
    { atMs: 0, key: 'journey.captions.attach.1' },
    { atMs: 6215, key: 'journey.captions.attach.2' },
    { atMs: 10339, key: 'journey.captions.attach.3' },
    { atMs: 11541, key: 'journey.captions.attach.4' },
  ],
  'journey-library': [
    { atMs: 0, key: 'journey.captions.library.1' },
    { atMs: 7669, key: 'journey.captions.library.2' },
    { atMs: 11307, key: 'journey.captions.library.3' },
    { atMs: 26607, key: 'journey.captions.library.4' },
    { atMs: 35350, key: 'journey.captions.library.5' },
    { atMs: 36860, key: 'journey.captions.library.6' },
    { atMs: 42413, key: 'journey.captions.library.7' },
    { atMs: 47282, key: 'journey.captions.library.8' },
    { atMs: 55199, key: 'journey.captions.library.9' },
    { atMs: 62434, key: 'journey.captions.library.10' },
  ],
  'journey-create-agent': [
    { atMs: 0, key: 'journey.captions.createAgent.1' },
    { atMs: 6543, key: 'journey.captions.createAgent.2' },
    { atMs: 10419, key: 'journey.captions.createAgent.3' },
    { atMs: 20409, key: 'journey.captions.createAgent.4' },
    { atMs: 30475, key: 'journey.captions.createAgent.5' },
    { atMs: 36211, key: 'journey.captions.createAgent.6' },
  ],
  'journey-agent-powers': [
    { atMs: 0, key: 'journey.captions.agentPowers.1' },
    { atMs: 6284, key: 'journey.captions.agentPowers.2' },
    { atMs: 10740, key: 'journey.captions.agentPowers.3' },
    { atMs: 19213, key: 'journey.captions.agentPowers.4' },
    { atMs: 23968, key: 'journey.captions.agentPowers.5' },
    { atMs: 28686, key: 'journey.captions.agentPowers.6' },
  ],
  'journey-share-agent': [
    { atMs: 0, key: 'journey.captions.shareAgent.1' },
    { atMs: 5620, key: 'journey.captions.shareAgent.2' },
    { atMs: 10255, key: 'journey.captions.shareAgent.3' },
    { atMs: 17081, key: 'journey.captions.shareAgent.4' },
    { atMs: 21921, key: 'journey.captions.shareAgent.5' },
  ],
  'journey-settings': [
    { atMs: 0, key: 'journey.captions.settings.1' },
    { atMs: 6985, key: 'journey.captions.settings.2' },
    { atMs: 15234, key: 'journey.captions.settings.3' },
    { atMs: 23793, key: 'journey.captions.settings.4' },
  ],
};

export interface JourneyStepDef {
  id: string;
  /**
   * film   — watch, then do it yourself.
   * guided — the live product is the lesson; do it yourself.
   * notice — tells you something and asks for nothing. Completed by
   *          acknowledging it, which is how "here is what your role can do"
   *          gets delivered without inventing a gesture to demand for it.
   */
  kind: 'film' | 'guided' | 'notice';
  /** Film for this step. Missing file ⇒ the step degrades to its guided card. */
  clip?: JourneyClipId;
  /** The cb:* event that completes the Try. Absent on a notice. */
  detection?: JourneyEventName;
  /** Tenant-relative route where the Try happens ('' = home). */
  route: string;
  /** data-testid the glow ring anchors to, when one exists on that surface. */
  anchorTestId?: string;
  /** Feature flags this step needs, beyond its stage's. */
  flags?: string[];
  /** i18n keys (journey.steps.<id>.*): title, body, tryPrompt. */
}

export interface JourneyStageDef {
  id: number;
  slug: string;
  /** Feature flags that must ALL be on for this stage to exist for a tenant. */
  eligibility: string[];
  steps: JourneyStepDef[];
}

/* ══════════════════════ steps, declared once ══════════════════════ */

const ORIENT: JourneyStepDef = {
  id: 'orient',
  kind: 'film',
  clip: 'journey-orient',
  detection: JOURNEY_EVENTS.conversationStarted,
  route: '',
  // One card, not the whole gallery: a glow marker points at a control
  // someone can click, never at a page region.
  anchorTestId: 'agent-card',
};

const ASK: JourneyStepDef = {
  id: 'ask',
  kind: 'guided',
  detection: JOURNEY_EVENTS.messageSent,
  route: '',
};

const MODEL_SWITCH: JourneyStepDef = {
  id: 'modelSwitch',
  kind: 'film',
  clip: 'journey-model-switch',
  detection: JOURNEY_EVENTS.modelSwitched,
  route: '',
  anchorTestId: 'model-selector-trigger',
};

const ATTACH: JourneyStepDef = {
  id: 'attach',
  kind: 'guided',
  detection: JOURNEY_EVENTS.fileAttached,
  route: '',
  anchorTestId: 'chat-attach-button',
};

const LIBRARY: JourneyStepDef = {
  // SHOW, don't require: the film tours the Library (folders, summaries,
  // sharing to people and organizations, linking to agents and skills) and
  // the Try is simply opening it. Nobody is forced to upload anything.
  id: 'library',
  kind: 'film',
  clip: 'journey-library',
  detection: JOURNEY_EVENTS.libraryVisited,
  route: '/vault',
};

const CREATE_AGENT: JourneyStepDef = {
  id: 'createAgent',
  kind: 'film',
  clip: 'journey-create-agent',
  detection: JOURNEY_EVENTS.agentCreated,
  route: '/bots',
  anchorTestId: 'bots-create-button',
};

const AGENT_POWERS: JourneyStepDef = {
  id: 'agentPowers',
  kind: 'film',
  clip: 'journey-agent-powers',
  detection: JOURNEY_EVENTS.agentEditOpened,
  route: '/bots',
  anchorTestId: 'bot-action-edit',
};

const AOA_BUILD: JourneyStepDef = {
  id: 'aoaBuild',
  kind: 'guided',
  detection: JOURNEY_EVENTS.aoaMessageSent,
  route: '/agent-of-agents',
  flags: ['modules.agent_of_agents'],
};

const PROJECT: JourneyStepDef = {
  id: 'project',
  kind: 'guided',
  detection: JOURNEY_EVENTS.projectCreated,
  route: '/projects',
  anchorTestId: 'projects-create-button',
  flags: ['modules.projects'],
};

const INSIGHTS: JourneyStepDef = {
  id: 'insights',
  kind: 'guided',
  detection: JOURNEY_EVENTS.insightsViewed,
  route: '/insights',
  flags: ['modules.insights'],
};

const SETTINGS: JourneyStepDef = {
  id: 'settings',
  kind: 'guided',
  detection: JOURNEY_EVENTS.settingsOpened,
  route: '/settings',
};

/* ── the "what your role is" notices. One per audience, and the whole
      reason `notice` exists: a person should be told what they administer
      before being shown where to administer it. ── */

const SCOPE_USER: JourneyStepDef = { id: 'scopeUser', kind: 'notice', route: '' };
const SCOPE_ADMIN: JourneyStepDef = { id: 'scopeAdmin', kind: 'notice', route: '' };
const SCOPE_GROUP: JourneyStepDef = { id: 'scopeGroup', kind: 'notice', route: '' };

/* ── administration steps ── */

const ADMIN_OPEN: JourneyStepDef = {
  id: 'adminOpen',
  kind: 'guided',
  detection: JOURNEY_EVENTS.adminOpened,
  route: '/admin',
};

const ADMIN_USERS: JourneyStepDef = {
  id: 'adminUsers',
  kind: 'guided',
  detection: JOURNEY_EVENTS.adminUsersViewed,
  route: '/admin',
  anchorTestId: 'admin-tab-users',
};

const ADMIN_HIERARCHY: JourneyStepDef = {
  id: 'adminHierarchy',
  kind: 'guided',
  detection: JOURNEY_EVENTS.adminHierarchyViewed,
  route: '/admin',
  anchorTestId: 'admin-tab-hierarchy',
};

/* ══════════════════════ the three chains ══════════════════════ */

/**
 * A member. They talk to agents someone else built, and their conversations
 * are their own. Nothing here teaches a control they do not have — no
 * creating agents, no Library, no administration.
 */
const USER_CHAIN: readonly JourneyStageDef[] = [
  { id: 1, slug: 'agents', eligibility: [], steps: [ORIENT, ASK] },
  { id: 2, slug: 'chat', eligibility: [], steps: [MODEL_SWITCH, ATTACH] },
  { id: 3, slug: 'yours', eligibility: [], steps: [SCOPE_USER, PROJECT, SETTINGS] },
];

/**
 * An administrator of ONE tenant. Everything a member learns, then the things
 * only they can do: build agents, keep the knowledge, run the people.
 */
const ADMIN_CHAIN: readonly JourneyStageDef[] = [
  { id: 1, slug: 'agents', eligibility: [], steps: [ORIENT, ASK] },
  { id: 2, slug: 'chat', eligibility: [], steps: [MODEL_SWITCH, ATTACH] },
  { id: 3, slug: 'library', eligibility: ['modules.library'], steps: [LIBRARY] },
  { id: 4, slug: 'create', eligibility: [], steps: [CREATE_AGENT, AGENT_POWERS] },
  { id: 5, slug: 'tenant', eligibility: [], steps: [SCOPE_ADMIN, ADMIN_OPEN, ADMIN_USERS, PROJECT] },
];

/**
 * A group administrator. The admin chain, then the two things genuinely
 * theirs: a scope reaching every organization beneath them, and the wider
 * tooling built on top of it.
 */
const GROUP_ADMIN_CHAIN: readonly JourneyStageDef[] = [
  { id: 1, slug: 'agents', eligibility: [], steps: [ORIENT, ASK] },
  { id: 2, slug: 'chat', eligibility: [], steps: [MODEL_SWITCH, ATTACH] },
  { id: 3, slug: 'library', eligibility: ['modules.library'], steps: [LIBRARY] },
  { id: 4, slug: 'create', eligibility: [], steps: [CREATE_AGENT, AGENT_POWERS, AOA_BUILD] },
  { id: 5, slug: 'org', eligibility: [], steps: [SCOPE_GROUP, ADMIN_OPEN, ADMIN_HIERARCHY] },
  { id: 6, slug: 'wider', eligibility: [], steps: [PROJECT, INSIGHTS] },
];

export const JOURNEY_CHAINS: Record<JourneyAudience, readonly JourneyStageDef[]> = {
  user: USER_CHAIN,
  admin: ADMIN_CHAIN,
  group_admin: GROUP_ADMIN_CHAIN,
};

/** The audience a role runs. super_admin is above group_admin, not beside it. */
export function audienceForRole(role: string | undefined): JourneyAudience {
  if (role === 'group_admin' || role === 'super_admin') return 'group_admin';
  if (role === 'admin') return 'admin';
  return 'user';
}

/** Neuron awards — Signal's economy, renamed. */
export const NEURONS_TRY = 3;
export const NEURONS_ASSISTED = 1;
export const NEURONS_EXPLORED = 5;

/** Rank slugs in order. Thresholds are derived per eligible chain. */
export const RANKS = ['visitor', 'thinker', 'builder', 'mastermind'] as const;
export type Rank = (typeof RANKS)[number];

export interface EligibleStep extends JourneyStepDef {
  stageId: number;
  stageSlug: string;
}

/**
 * Derive the eligible chain for this person: their role picks the chain, then
 * their tenant's flags prune it. A stage with no surviving steps folds out
 * entirely.
 */
export function deriveChain(
  audience: JourneyAudience,
  hasFeature: (flag: string) => boolean,
): {
  steps: EligibleStep[];
  stages: { id: number; slug: string; steps: EligibleStep[] }[];
} {
  const source = JOURNEY_CHAINS[audience];
  const stages: { id: number; slug: string; steps: EligibleStep[] }[] = [];
  for (const stage of source) {
    if (!stage.eligibility.every(hasFeature)) continue;
    const steps = stage.steps
      .filter((s) => (s.flags ?? []).every(hasFeature))
      .map((s) => ({ ...s, stageId: stage.id, stageSlug: stage.slug }));
    if (steps.length === 0) continue;
    stages.push({ id: stage.id, slug: stage.slug, steps });
  }
  return { steps: stages.flatMap((s) => s.steps), stages };
}

/**
 * Rank for a Neuron total, derived from the chain this person is actually
 * running. Thinker at the end of the conversation stages, Builder once the
 * making-things stages are done, Mastermind at completion — every threshold
 * lands on a real step boundary in every chain because it is computed from
 * that chain rather than stamped.
 */
export function deriveRank(neurons: number, chain: EligibleStep[]): Rank {
  const total = chain.length * NEURONS_TRY;
  if (total === 0) return 'visitor';
  const thinkerAt = chain.filter((s) => s.stageId <= 2).length * NEURONS_TRY;
  const builderAt = chain.filter((s) => s.stageId <= 4).length * NEURONS_TRY;
  if (neurons >= total) return 'mastermind';
  if (neurons >= builderAt && builderAt > thinkerAt) return 'builder';
  if (neurons >= thinkerAt && thinkerAt > 0) return 'thinker';
  return 'visitor';
}

